// ─── PART 9: SYNTHETIC SHORT & SQUEEZE SCANNER ───────────────────────────────
// Dependencies: part1_core (C, s, fmt, pct, fmtK, fmtB, DataStatus, AnalystButton)
// Exports: SqueezeTab
//
// Data sources:
//   Yahoo Finance     — float, shares outstanding, short interest % (summary)
//   Yahoo Finance     — options chain (put OI, call OI, IV skew)
//   FINRA             — short interest data file (twice monthly, free download)
//   Alpaca REST       — price, volume (for days-to-cover)
//   Tradier           — options chain with Greeks (primary if key present)
//
// onData callbacks pushed to globalSignals:
//   "squeezeScans"    — array of scored ticker results
//
// Signal components:
//   1. Short Interest % of Float       — >80% elevated, >100% synthetic confirmed
//   2. Put OI × 100 vs Float           — synthetic put detection
//   3. Put/Call IV Skew                — elevated put premium = borrow stress
//   4. Days to Cover                   — short interest ÷ avg daily volume
//   5. OTM Call OI Buildup             — unusual accumulation on cheap strikes
//   6. Vol/OI Ratio anomalies          — unusual activity screen
//   7. GEX flip risk                   — negative GEX zones near current price
//
// Squeeze Pressure Score: 0-100 composite
//   Each component contributes weighted points
//   Score tiers: WATCH (20+), ELEVATED (40+), FLAG (60+), EXTREME (80+)
//
// FINRA short interest:
//   Downloaded twice monthly from FINRA (finra.org)
//   User pastes the raw text or CSV into the parser
//   Terminal extracts relevant tickers and updates scores
//   2-week lag acknowledged — structural confirmation layer only
//   Real-time options data is the timing layer

import { useState, useEffect, useCallback, useRef } from "react";
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, Cell,
} from "recharts";
import { AlertCircle, RefreshCw, Upload, Search } from "lucide-react";
import {
  C, s, font, fmt, pct, fmtK, fmtB,
  DataStatus, AnalystButton,
  ALPACA_REST, TRADIER_BASE, backendFetch,
} from "./core";
import { DarkPoolPanel } from "./AltDataTab";

// ─── SCORE WEIGHTS ────────────────────────────────────────────────────────────
// Each signal contributes max points to the 0-100 composite score
const WEIGHTS = {
  shortInterest:    25,   // Short interest % of float — primary structural signal
  syntheticPuts:    20,   // Put OI exceeds float — synthetic confirmation
  daysTocover:      15,   // Exit pain — how long for shorts to unwind
  ivSkew:           15,   // Put IV vs Call IV premium — borrow stress proxy
  otmCallBuildup:   15,   // OTM call OI accumulation — positioning signal
  unusualVolume:    10,   // Vol/OI ratio anomalies — activity spike
};

// ─── SCORE TIERS ─────────────────────────────────────────────────────────────
const TIERS = [
  { min: 80, label: "EXTREME",  color: C.red,    desc: "Multiple high-conviction signals aligned. Structurally similar to pre-squeeze setups." },
  { min: 60, label: "FLAG",     color: C.orange, desc: "Strong confluence across structural and timing signals. Warrants close monitoring." },
  { min: 40, label: "ELEVATED", color: C.amber,  desc: "Multiple signals present. Incomplete picture — watch for additional confirmation." },
  { min: 20, label: "WATCH",    color: C.teal,   desc: "Early signals present. Single-source — could be noise. Add to monitor list." },
  { min: 0,  label: "NEUTRAL",  color: C.textDim,desc: "No significant squeeze signals detected at current data snapshot." },
];

function getTier(score) {
  return TIERS.find((t) => score >= t.min) || TIERS[TIERS.length - 1];
}

// ─── EMPTY SCAN RESULT ────────────────────────────────────────────────────────
// ⚠⚠ HIDE-WHEN-NULL. A field renders only when it holds a real number; null,
// undefined, NaN and strings are ABSENT, never "—", "N/A" or a fake 0. A real 0
// is a number and still displays. Local on purpose: the shared fmtK prints "0"
// for NaN ((n || 0).toString()), so values are screened here before formatting.
const real = (v) => typeof v === "number" && Number.isFinite(v);

function emptyResult(ticker) {
  return {
    ticker,
    score:           0,
    tier:            TIERS[TIERS.length - 1],
    timestamp:       null,
    status:          "pending",
    error:           null,
    // Raw data
    price:           null,
    float:           null,
    sharesOut:       null,
    avgVolume:       null,
    shortInterestPct: null,    // % of float (from Yahoo summary or FINRA paste)
    shortInterestSrc: null,    // "yahoo" | "finra" | null
    // Computed signals
    putOI:           null,
    callOI:          null,
    putOIvsFloat:    null,     // putOI × 100 / float — key synthetic detection metric
    daysTocover:     null,
    putIVavg:        null,
    callIVavg:       null,
    ivSkewRatio:     null,     // putIV / callIV — elevated = borrow stress
    otmCallOI:       null,     // OI on calls >10% OTM
    unusualStrikes:  [],       // Vol/OI > 3x
    // Score breakdown
    breakdown:       {},
    // Chain data for display
    topPuts:         [],
    topCalls:        [],
    // Backend deep-dive record (/api/squeeze/{symbol}) — null until merged, and
    // null forever for a symbol the nightly pre-score has never seen.
    platform:        null,
  };
}

// ─── YAHOO FINANCE QUOTE FETCH ────────────────────────────────────────────────
// Pulls: price, float, shares outstanding, short interest, avg volume
async function fetchYahooSummary(ticker) {
  try {
    const res  = await fetch(
      `https://query1.finance.yahoo.com/v10/finance/quoteSummary/${ticker}` +
      `?modules=defaultKeyStatistics,summaryDetail,price`
    );
    const data = await res.json();
    const ks   = data?.quoteSummary?.result?.[0]?.defaultKeyStatistics;
    const sd   = data?.quoteSummary?.result?.[0]?.summaryDetail;
    const pr   = data?.quoteSummary?.result?.[0]?.price;

    return {
      price:            pr?.regularMarketPrice?.raw ?? null,
      float:            ks?.floatShares?.raw ?? null,
      sharesOut:        ks?.sharesOutstanding?.raw ?? null,
      avgVolume:        sd?.averageVolume?.raw ?? null,
      shortInterestPct: ks?.shortPercentOfFloat?.raw
                          ? ks.shortPercentOfFloat.raw * 100
                          : null,
      shortInterestAbs: ks?.sharesShort?.raw ?? null,
      shortRatio:       ks?.shortRatio?.raw ?? null,     // days to cover from Yahoo
    };
  } catch {
    return null;
  }
}

// ─── OPTIONS CHAIN FETCH ──────────────────────────────────────────────────────
// Tradier primary (real Greeks), Yahoo fallback
// Returns aggregated metrics across nearest 3 expirations
async function fetchOptionsMetrics(ticker, spot, tradierKey) {
  let calls = [], puts = [];
  let gotData = false;

  // Tradier — real Greeks, more reliable
  if (tradierKey) {
    try {
      // Get expirations
      const expRes  = await fetch(
        `${TRADIER_BASE}/markets/options/expirations?symbol=${ticker}`,
        { headers: { "Authorization": `Bearer ${tradierKey}`, "Accept": "application/json" } }
      );
      const expData = await expRes.json();
      const rawExps = expData?.expirations?.date;
      if (rawExps) {
        const exps = (Array.isArray(rawExps) ? rawExps : [rawExps]).slice(0, 3);

        // Fetch first 3 expirations and aggregate
        for (const exp of exps) {
          const res  = await fetch(
            `${TRADIER_BASE}/markets/options/chains?symbol=${ticker}&expiration=${exp}&greeks=true`,
            { headers: { "Authorization": `Bearer ${tradierKey}`, "Accept": "application/json" } }
          );
          const data = await res.json();
          const opts = data?.options?.option;
          if (opts?.length) {
            calls.push(...opts.filter((o) => o.option_type === "call").map((o) => ({
              strike:    o.strike,
              oi:        o.open_interest || 0,
              volume:    o.volume || 0,
              iv:        o.greeks?.mid_iv || null,
              delta:     o.greeks?.delta || null,
              expiration: exp,
            })));
            puts.push(...opts.filter((o) => o.option_type === "put").map((o) => ({
              strike:    o.strike,
              oi:        o.open_interest || 0,
              volume:    o.volume || 0,
              iv:        o.greeks?.mid_iv || null,
              delta:     o.greeks?.delta || null,
              expiration: exp,
            })));
            gotData = true;
          }
        }
      }
    } catch {}
  }

  // Yahoo fallback — nearest expiration only
  if (!gotData) {
    try {
      const res    = await fetch(
        `https://query1.finance.yahoo.com/v7/finance/options/${ticker}`
      );
      const data   = await res.json();
      const result = data?.optionChain?.result?.[0];
      if (result?.options?.[0]) {
        const ySpot = result.quote?.regularMarketPrice || spot;

        // Fetch up to 3 expirations
        const expDates = (result.expirationDates || []).slice(0, 3);
        for (const expTs of expDates) {
          const expRes = await fetch(
            `https://query1.finance.yahoo.com/v7/finance/options/${ticker}?date=${expTs}`
          );
          const expData = await expRes.json();
          const expResult = expData?.optionChain?.result?.[0];
          if (expResult?.options?.[0]) {
            const exp = new Date(expTs * 1000).toISOString().slice(0, 10);
            calls.push(...(expResult.options[0].calls || []).map((c) => ({
              strike:    c.strike,
              oi:        c.openInterest || 0,
              volume:    c.volume || 0,
              iv:        c.impliedVolatility || null,
              delta:     null,
              expiration: exp,
            })));
            puts.push(...(expResult.options[0].puts || []).map((p) => ({
              strike:    p.strike,
              oi:        p.openInterest || 0,
              volume:    p.volume || 0,
              iv:        p.impliedVolatility || null,
              delta:     null,
              expiration: exp,
            })));
            gotData = true;
          }
        }
      }
    } catch {}
  }

  if (!gotData || (!calls.length && !puts.length)) return null;

  // ── Aggregate metrics ──
  const totalPutOI  = puts.reduce((a, p) => a + p.oi, 0);
  const totalCallOI = calls.reduce((a, c) => a + c.oi, 0);

  // IV averages — ATM ± 20%
  const atmPuts  = puts.filter((p)  => spot && Math.abs(p.strike - spot) / spot < 0.2 && p.iv);
  const atmCalls = calls.filter((c) => spot && Math.abs(c.strike - spot) / spot < 0.2 && c.iv);
  const putIVavg  = atmPuts.length  ? atmPuts.reduce((a, p)  => a + p.iv,  0) / atmPuts.length  : null;
  const callIVavg = atmCalls.length ? atmCalls.reduce((a, c) => a + c.iv, 0) / atmCalls.length : null;

  // OTM call OI — calls more than 10% above spot
  // ⚠ null without a spot: "10% above spot" is undefined then, and the empty
  // filter used to sum to a fake 0 (a 0-point "0.00% of float" breakdown row).
  const otmCalls    = calls.filter((c) => spot && c.strike > spot * 1.10);
  const otmCallOI   = spot ? otmCalls.reduce((a, c) => a + c.oi, 0) : null;

  // Unusual volume — Vol/OI > 3x, min 100 OI
  const unusual = [...calls, ...puts]
    .filter((o) => o.oi > 100 && o.volume > 0 && o.volume / o.oi > 3)
    .sort((a, b) => (b.volume / b.oi) - (a.volume / a.oi))
    .slice(0, 6)
    .map((o) => ({
      ...o,
      isCall: o.strike < (spot || 0) ? false : true,
      ratio:  o.volume / o.oi,
    }));

  // Top puts and calls by OI for display
  const topPuts  = [...puts].sort((a, b) => b.oi - a.oi).slice(0, 5);
  const topCalls = [...calls].sort((a, b) => b.oi - a.oi).slice(0, 5);

  return {
    totalPutOI,
    totalCallOI,
    putIVavg,
    callIVavg,
    ivSkewRatio: putIVavg && callIVavg ? putIVavg / callIVavg : null,
    otmCallOI,
    unusual,
    topPuts,
    topCalls,
  };
}

// ─── SCORE COMPUTATION ────────────────────────────────────────────────────────
function computeScore(result) {
  const breakdown = {};
  let score = 0;

  // 1. Short Interest % of Float (max 25pts)
  if (result.shortInterestPct != null) {
    const si = result.shortInterestPct;
    let pts = 0;
    if      (si >= 140) pts = 25;   // GME-level — synthetic confirmed extreme
    else if (si >= 100) pts = 22;   // Synthetics confirmed
    else if (si >= 80)  pts = 16;   // Elevated — approaching synthetic territory
    else if (si >= 50)  pts = 10;   // High but not extreme
    else if (si >= 30)  pts = 4;    // Elevated for context
    breakdown.shortInterest = { pts, max: WEIGHTS.shortInterest, value: `${fmt(si, 1)}% of float`, flag: si >= 100 };
    score += pts;
  }

  // 2. Put OI vs Float — synthetic put detection (max 20pts)
  if (result.putOIvsFloat != null) {
    const ratio = result.putOIvsFloat;   // putOI × 100 / float
    let pts = 0;
    if      (ratio >= 1.5) pts = 20;    // Put OI represents 150%+ of float — extreme synthetic signal
    else if (ratio >= 1.0) pts = 17;    // Exceeds float — synthetics mathematically confirmed
    else if (ratio >= 0.7) pts = 12;    // 70%+ — very high put exposure
    else if (ratio >= 0.4) pts = 7;     // Elevated
    else if (ratio >= 0.2) pts = 3;     // Slight elevation
    breakdown.syntheticPuts = { pts, max: WEIGHTS.syntheticPuts, value: `${fmt(ratio * 100, 1)}% of float`, flag: ratio >= 1.0 };
    score += pts;
  }

  // 3. Days to Cover (max 15pts)
  if (result.daysTocover != null) {
    const dtc = result.daysTocover;
    let pts = 0;
    if      (dtc >= 20) pts = 15;   // Extreme — shorts cannot exit without massive impact
    else if (dtc >= 10) pts = 12;   // Very high exit pain
    else if (dtc >= 7)  pts = 8;    // Elevated
    else if (dtc >= 5)  pts = 5;    // Notable
    else if (dtc >= 3)  pts = 2;    // Slight
    breakdown.daysTocover = { pts, max: WEIGHTS.daysTocover, value: `${fmt(dtc, 1)} days`, flag: dtc >= 10 };
    score += pts;
  }

  // 4. IV Skew — put IV vs call IV (max 15pts)
  if (result.ivSkewRatio != null) {
    const skew = result.ivSkewRatio;
    let pts = 0;
    if      (skew >= 2.5) pts = 15;  // Put IV more than 2.5x call IV — extreme borrow stress signal
    else if (skew >= 2.0) pts = 12;  // Very elevated put premium
    else if (skew >= 1.5) pts = 8;   // Elevated — notable borrow pressure
    else if (skew >= 1.3) pts = 4;   // Slightly elevated
    else if (skew >= 1.1) pts = 1;   // Marginal
    breakdown.ivSkew = { pts, max: WEIGHTS.ivSkew, value: `${fmt(skew, 2)}x (put/call IV)`, flag: skew >= 1.5 };
    score += pts;
  }

  // 5. OTM Call Buildup (max 15pts)
  if (result.otmCallOI != null && result.float != null) {
    // OTM call OI as % of float — measures speculative long positioning
    const otmRatio = (result.otmCallOI * 100) / result.float;
    let pts = 0;
    if      (otmRatio >= 0.5)  pts = 15;   // Massive OTM call positioning
    else if (otmRatio >= 0.3)  pts = 12;   // Very high speculative positioning
    else if (otmRatio >= 0.15) pts = 8;    // Elevated
    else if (otmRatio >= 0.08) pts = 4;    // Notable
    else if (otmRatio >= 0.03) pts = 2;    // Slight
    breakdown.otmCallBuildup = { pts, max: WEIGHTS.otmCallBuildup, value: `${fmt(otmRatio * 100, 2)}% of float`, flag: otmRatio >= 0.15 };
    score += pts;
  }

  // 6. Unusual Volume (max 10pts)
  if (result.unusualStrikes?.length > 0) {
    const topRatio = result.unusualStrikes[0]?.ratio || 0;
    let pts = 0;
    if      (result.unusualStrikes.length >= 4 && topRatio >= 10) pts = 10;
    else if (result.unusualStrikes.length >= 3 && topRatio >= 5)  pts = 7;
    else if (result.unusualStrikes.length >= 2)                    pts = 4;
    else if (result.unusualStrikes.length >= 1)                    pts = 2;
    breakdown.unusualVolume = { pts, max: WEIGHTS.unusualVolume, value: `${result.unusualStrikes.length} strikes (top ${fmt(topRatio, 1)}x)`, flag: topRatio >= 10 };
    score += pts;
  }

  return { score: Math.min(100, Math.round(score)), breakdown };
}

// ─── FULL TICKER SCAN ─────────────────────────────────────────────────────────
async function scanTicker(ticker, keys, finraData) {
  const result = emptyResult(ticker);
  result.status = "scanning";

  try {
    // Step 1: Yahoo summary — float, short interest, price, avg volume
    const summary = await fetchYahooSummary(ticker);
    if (summary) {
      result.price            = summary.price;
      result.float            = summary.float;
      result.sharesOut        = summary.sharesOut;
      result.avgVolume        = summary.avgVolume;
      result.shortInterestPct = summary.shortInterestPct;
      result.shortInterestSrc = summary.shortInterestPct != null ? "yahoo" : null;

      // Days to cover — prefer Yahoo's shortRatio, fallback to manual compute
      if (summary.shortRatio) {
        result.daysTocover = summary.shortRatio;
      } else if (summary.shortInterestAbs && summary.avgVolume) {
        result.daysTocover = summary.shortInterestAbs / summary.avgVolume;
      }
    }

    // Step 2: Override short interest with FINRA paste data if available
    if (finraData && finraData[ticker.toUpperCase()]) {
      const fd = finraData[ticker.toUpperCase()];
      // FINRA gives raw short interest shares — compute % of float
      if (fd.shortShares && result.float) {
        result.shortInterestPct = (fd.shortShares / result.float) * 100;
        result.shortInterestSrc = "finra";
      }
      if (fd.daysTocover) result.daysTocover = fd.daysTocover;
    }

    // Step 3: Alpaca price/volume if Yahoo failed (backend holds credentials)
    if (!result.price && window.QUANT_SIGNAL_BACKEND) {
      try {
        const res  = await backendFetch(
          `${ALPACA_REST}/stocks/snapshots?symbols=${ticker}`,
          `/api/snapshot?symbols=${ticker}`
        );
        const data = await res.json();
        const snap = data[ticker];
        if (snap) result.price = snap.latestTrade?.p;
      } catch {}
    }

    // Step 4: Options chain metrics
    const opts = await fetchOptionsMetrics(ticker, result.price, keys.tradierKey);
    if (opts) {
      result.putOI       = opts.totalPutOI;
      result.callOI      = opts.totalCallOI;
      result.putIVavg    = opts.putIVavg;
      result.callIVavg   = opts.callIVavg;
      result.ivSkewRatio = opts.ivSkewRatio;
      result.otmCallOI   = opts.otmCallOI;
      result.unusualStrikes = opts.unusual;
      result.topPuts     = opts.topPuts;
      result.topCalls    = opts.topCalls;

      // Synthetic put detection: put OI × 100 (contract multiplier) vs float
      if (result.float && result.putOI) {
        result.putOIvsFloat = (result.putOI * 100) / result.float;
      }
    }

    // Step 5: Compute composite score
    const { score, breakdown } = computeScore(result);
    result.score     = score;
    result.breakdown = breakdown;
    result.tier      = getTier(score);
    result.timestamp = new Date().toLocaleTimeString();
    result.status    = "done";

  } catch (e) {
    result.status = "error";
    result.error  = e.message;
  }

  return result;
}

// ─── PLATFORM SQUEEZE RECORD ──────────────────────────────────────────────────
// ⚠⚠ The deep dive derived short interest and float by hitting Yahoo DIRECTLY from
// the browser, so a candidate scored on FINRA + EDGAR opened a panel that threw
// those away and recomputed weaker numbers from a different source. (Yahoo short
// interest is also what produced the 2026-07-21 "SI 408%" bug; the FINRA + EDGAR
// path is the hardened one.) This pulls the platform's own record so the row and
// the panel agree.
//
// Best-effort throughout: null on any failure, and the existing scan renders
// exactly as it did before.
// ⚠ `_source: "empty"` means the nightly pre-score has never seen this symbol —
// normal for a hand-typed ticker. Treated as "no record" so nothing platform-side
// renders, rather than a panel full of dashes.
async function fetchPlatformSqueeze(ticker) {
  try {
    const res = await backendFetch("https://data.alpaca.markets", `/api/squeeze/${ticker}`);
    if (!res.ok) return null;
    const d = await res.json();
    if (!d || typeof d !== "object" || !d.symbol) return null;
    return d._source === "empty" ? null : d;
  } catch {
    return null;
  }
}

// ⚠⚠ Only FOUR fields are merged, and the omissions are the point — three endpoint
// fields LOOK mappable and are not:
//   put_oi_vs_float  endpoint returns a PERCENT (118.4); putOIvsFloat here is a
//                    FRACTION (1.0 = 100% of float — see computeScore's thresholds
//                    and the badge's `* 100`). Assigning it directly renders
//                    "11840% of float" and pins every row to "⚠ SYNTHETIC".
//   otm_call_oi      endpoint counts EVERY call above spot; otmCallOI here means
//                    calls >10% OTM and feeds (otmCallOI * 100)/float in
//                    computeScore, so the larger set inflates the score.
//   iv_skew_ratio    endpoint uses OTM puts 5-15% below spot over ATM calls ±2%;
//                    this file uses mean put IV over mean call IV. Feeding it to
//                    computeScore's thresholds silently shifts every score.
// All three stay locally computed. The endpoint's versions are DISPLAYED in the
// platform block under their own labels, never merged into the scored fields.
//
// ⚠ Re-scoring after the merge is mandatory, not tidiness: scanTicker computes
// putOIvsFloat from ITS float and then scores, so overriding float / SI / DTC
// afterwards without re-running computeScore would show FINRA inputs beside a
// score derived from Yahoo's.
function applyPlatform(result, p) {
  if (!p) return result;
  const num = (v) => (typeof v === "number" && isFinite(v) ? v : null);
  const r = { ...result, platform: p };

  if (num(p.short_float_pct) != null) {
    r.shortInterestPct = p.short_float_pct;   // FINRA consolidated
    r.shortInterestSrc = "platform";
  }
  if (num(p.dtc) != null) r.daysTocover = p.dtc;          // FINRA
  if (num(p.float_shares)) r.float = p.float_shares;      // SEC EDGAR
  if (r.price == null && num(p.spot) != null) r.price = p.spot;

  if (r.float && r.putOI) r.putOIvsFloat = (r.putOI * 100) / r.float;
  const { score, breakdown } = computeScore(r);
  r.score     = score;
  r.breakdown = breakdown;
  r.tier      = getTier(score);
  return r;
}

// One scan plus its platform record, merged. Used by BOTH entry points into the
// deep-dive view — the tapped-candidate path and the SCAN ALL loop — so a symbol
// shows the same numbers however it got there.
// ⚠ allSettled, not all: an endpoint outage must degrade to the pre-existing
// behaviour, never take the scan down with it.
async function scanWithPlatform(ticker, keys, finraData) {
  const [scan, plat] = await Promise.allSettled([
    scanTicker(ticker, keys, finraData),
    fetchPlatformSqueeze(ticker),
  ]);
  const base = scan.status === "fulfilled" && scan.value
    ? scan.value
    : { ...emptyResult(ticker), status: "error", error: "Scan failed" };
  return applyPlatform(base, plat.status === "fulfilled" ? plat.value : null);
}

// ─── FINRA PASTE PARSER ───────────────────────────────────────────────────────
// Parses raw FINRA short interest report text/CSV
// FINRA format: Symbol | Company | Date | ShortInterest | AvgDailyVol | DaysTocover
function parseFinraData(raw) {
  const lines  = raw.trim().split("\n");
  const result = {};

  for (const line of lines) {
    // Handle both pipe-delimited and comma-delimited
    const parts = line.includes("|") ? line.split("|") : line.split(",");
    if (parts.length < 4) continue;

    const symbol     = parts[0]?.trim().toUpperCase();
    const shortShares = parseInt(parts[3]?.trim().replace(/,/g, ""), 10);
    const avgVol     = parseInt(parts[4]?.trim().replace(/,/g, ""), 10);
    const dtc        = parseFloat(parts[5]?.trim());

    if (symbol && !isNaN(shortShares)) {
      result[symbol] = {
        shortShares,
        avgVol:     isNaN(avgVol) ? null : avgVol,
        daysTocover: isNaN(dtc)  ? null : dtc,
      };
    }
  }

  return result;
}

// ─── SCORE GAUGE ─────────────────────────────────────────────────────────────
function ScoreGauge({ score, tier }) {
  const segments = [
    { min: 0,  max: 20, color: C.textDim },
    { min: 20, max: 40, color: C.teal    },
    { min: 40, max: 60, color: C.amber   },
    { min: 60, max: 80, color: C.orange  },
    { min: 80, max: 100,color: C.red     },
  ];

  return (
    <div>
      {/* Score number */}
      <div style={{ fontSize: 36, fontWeight: 700, color: tier.color, lineHeight: 1 }}>
        {score}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: tier.color, marginBottom: 8 }}>
        {tier.label}
      </div>

      {/* Bar */}
      <div style={{ display: "flex", gap: 2, height: 6, marginBottom: 6 }}>
        {segments.map((seg, i) => {
          const filled = score >= seg.max
            ? 1
            : score > seg.min
            ? (score - seg.min) / (seg.max - seg.min)
            : 0;
          return (
            <div key={i} style={{ flex: 1, background: C.border, borderRadius: 1, overflow: "hidden" }}>
              <div style={{ width: `${filled * 100}%`, height: "100%", background: seg.color, transition: "width 0.4s" }} />
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.6 }}>
        {tier.desc}
      </div>
    </div>
  );
}

// ─── BREAKDOWN TABLE ─────────────────────────────────────────────────────────
function BreakdownTable({ breakdown }) {
  const rows = Object.entries(breakdown);
  if (!rows.length) return null;

  const labels = {
    shortInterest:   "Short Interest % Float",
    syntheticPuts:   "Put OI vs Float (Synthetic Detection)",
    daysTocover:     "Days to Cover",
    ivSkew:          "Put/Call IV Skew (Borrow Stress Proxy)",
    otmCallBuildup:  "OTM Call OI Buildup",
    unusualVolume:   "Unusual Vol/OI Activity",
  };

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
          {["SIGNAL", "VALUE", "SCORE", "MAX", "FLAG"].map((h) => (
            <th key={h} style={{ padding: "3px 7px", textAlign: h === "SIGNAL" || h === "VALUE" ? "left" : "right", fontSize: 11, color: C.textDim, fontWeight: 700 }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(([key, data]) => (
          <tr key={key} style={{ borderBottom: `1px solid ${C.border}`, background: data.flag ? C.red + "08" : "transparent" }}>
            <td style={{ padding: "6px 7px", color: C.textMid, fontSize: 13 }}>{labels[key] || key}</td>
            <td style={{ padding: "6px 7px", color: C.text, fontWeight: 600 }}>{data.value}</td>
            <td style={{ padding: "6px 7px", textAlign: "right", fontWeight: 700, color: data.pts > 0 ? C.amber : C.textDim }}>
              {data.pts}
            </td>
            <td style={{ padding: "6px 7px", textAlign: "right", color: C.textDim }}>{data.max}</td>
            <td style={{ padding: "6px 7px", textAlign: "right" }}>
              {data.flag && <span style={{ ...s.tag(C.red), fontSize: 10 }}>⚠ FLAG</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── RESULT CARD ─────────────────────────────────────────────────────────────
function ResultCard({ result, anthropicKey, expanded, onToggle }) {
  if (!result) return null;
  const tier = result.tier || getTier(0);

  const analystContext =
    `Squeeze/synthetic short analysis for ${result.ticker}:\n\n` +
    `Composite Squeeze Pressure Score: ${result.score}/100 (${tier.label})\n` +
    `Price: ${result.price ? `$${fmt(result.price)}` : "N/A"}\n` +
    `Float: ${result.float ? fmtK(result.float) : "N/A"} shares\n` +
    `Short Interest: ${result.shortInterestPct != null ? `${fmt(result.shortInterestPct, 1)}%` : "N/A"} of float` +
    (result.shortInterestSrc ? ` (source: ${result.shortInterestSrc})` : "") + "\n" +
    `Days to Cover: ${result.daysTocover != null ? fmt(result.daysTocover, 1) : "N/A"}\n` +
    `Put OI (3 expirations): ${result.putOI ? fmtK(result.putOI) : "N/A"} contracts\n` +
    `Put OI as % of float: ${result.putOIvsFloat != null ? `${fmt(result.putOIvsFloat * 100, 1)}%` : "N/A"}` +
    (result.putOIvsFloat >= 1.0 ? " ← EXCEEDS FLOAT (synthetic puts confirmed)" : "") + "\n" +
    `Call OI (3 expirations): ${result.callOI ? fmtK(result.callOI) : "N/A"} contracts\n` +
    `OTM Call OI (>10% OTM): ${result.otmCallOI ? fmtK(result.otmCallOI) : "N/A"} contracts\n` +
    `Put/Call IV Skew: ${result.ivSkewRatio != null ? `${fmt(result.ivSkewRatio, 2)}x` : "N/A"}\n` +
    `Unusual Vol/OI strikes: ${result.unusualStrikes?.length || 0}\n\n` +
    `Score breakdown:\n` +
    Object.entries(result.breakdown).map(([k, v]) => `  ${k}: ${v.pts}/${v.max} pts — ${v.value}${v.flag ? " [FLAGGED]" : ""}`).join("\n") +
    `\n\nAnalyze this squeeze setup: assess whether the synthetic short signals are confirmed, ` +
    `what the IV skew implies about borrow costs, whether the OTM call buildup is consistent with ` +
    `speculative long positioning, and how this compares mechanically to historical squeeze setups. ` +
    `Note any signal gaps (e.g. short interest lag) that would affect conviction. ` +
    `Be direct about signal strength and what would constitute confirmation or invalidation.`;

  return (
    <div style={{
      ...s.card(tier.color),
      borderLeft: `3px solid ${tier.color}`,
    }}>
      {/* Card header */}
      <div
        style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer" }}
        onClick={onToggle}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{result.ticker}</div>
            {real(result.price) && result.price > 0 && (
              <div style={{ fontSize: 13, color: C.textDim }}>${fmt(result.price)}</div>
            )}
          </div>
          <ScoreGauge score={result.score} tier={tier} />
        </div>

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          {result.status === "scanning" && (
            <div style={{ fontSize: 13, color: C.textDim }}>SCANNING...</div>
          )}
          {result.status === "error" && (
            <div style={{ fontSize: 13, color: C.red }}>ERROR</div>
          )}
          {result.status === "done" && (
            <div style={{ fontSize: 11, color: C.textDim }}>{result.timestamp}</div>
          )}
          <div style={{ fontSize: 13, color: C.textDim, marginTop: 4 }}>
            {expanded ? "▲ COLLAPSE" : "▼ EXPAND"}
          </div>
        </div>
      </div>

      {/* Quick flags row */}
      {result.status === "done" && (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
          {result.shortInterestPct != null && (
            <span style={s.tag(result.shortInterestPct >= 100 ? C.red : result.shortInterestPct >= 50 ? C.amber : C.textDim)}>
              SI: {fmt(result.shortInterestPct, 1)}%
            </span>
          )}
          {result.putOIvsFloat != null && (
            <span style={s.tag(result.putOIvsFloat >= 1.0 ? C.red : result.putOIvsFloat >= 0.5 ? C.amber : C.textDim)}>
              Put OI: {fmt(result.putOIvsFloat * 100, 0)}% of float{result.putOIvsFloat >= 1.0 ? " ⚠ SYNTHETIC" : ""}
            </span>
          )}
          {result.daysTocover != null && (
            <span style={s.tag(result.daysTocover >= 10 ? C.red : result.daysTocover >= 5 ? C.amber : C.textDim)}>
              DTC: {fmt(result.daysTocover, 1)}d
            </span>
          )}
          {result.ivSkewRatio != null && (
            <span style={s.tag(result.ivSkewRatio >= 1.5 ? C.red : result.ivSkewRatio >= 1.2 ? C.amber : C.textDim)}>
              IV Skew: {fmt(result.ivSkewRatio, 2)}x
            </span>
          )}
          {result.unusualStrikes?.length > 0 && (
            <span style={s.tag(C.amber)}>
              ⚡ {result.unusualStrikes.length} unusual strikes
            </span>
          )}
        </div>
      )}

      {/* Expanded detail */}
      {expanded && result.status === "done" && (
        <div style={{ marginTop: 12 }}>

          {/* Score breakdown */}
          <div style={{ fontSize: 13, color: C.amber, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 6 }}>
            SIGNAL BREAKDOWN
          </div>
          <BreakdownTable breakdown={result.breakdown} />

          {/* ── Platform signals — the backend's own record for this symbol ──
              ⚠ These are the four signals the panel used to discard entirely: the
              composite the CANDIDATES row was scored on, its dark-pool bias, its
              unusual-call flag and the early-squeeze tell. Rendered separately from
              the local scan rather than merged into it, because the two are scored
              on different inputs and conflating them is what produced the
              contradiction in the first place. Every line omits when null. */}
          {result.platform && (() => {
            // ⚠⚠ THE PRE-SCORE AND THE MARKET LAYERS COVER DISJOINT SYMBOLS.
            // A squeeze candidate is an illiquid small cap with a FINRA record;
            // a liquid name has options and dark-pool flow but no record. Neither
            // ever fills every field, so the panel now says WHICH it is looking at
            // instead of rendering the other half as a row of dashes.
            const p = result.platform;
            const isCandidate = p.base_score != null || p.scored_at != null;
            const ivLabel = p.iv_source === "yahoo"
              ? "IV SKEW (DELAYED)"      // Yahoo's published IV, not an OPRA mid
              : "IV SKEW (OTM/ATM)";
            // ⚠ Hide-when-null: a tile without a real value is dropped, not dashed.
            // Share counts must be positive (0 shares is not a reading); the skew
            // and put-OI ratio keep a real 0.
            const tiles = [
              ...(isCandidate ? [
                real(p.short_shares) && p.short_shares > 0 && { label: "SHORT SHARES", value: fmtK(p.short_shares) },
                real(p.float_shares) && p.float_shares > 0 && { label: "FLOAT (FILED)", value: fmtK(p.float_shares) },
              ] : []),
              // ⚠ Distinct labels on purpose — these are NOT the same numbers as
              // the IV SKEW / PUT OI badges above, which are computed locally
              // over different strike bands. Two skew figures on one screen is
              // the lesser evil against silently overwriting a scored input.
              real(p.iv_skew_ratio) && { label: ivLabel, value: `${fmt(p.iv_skew_ratio, 2)}x` },
              real(p.put_oi_vs_float) && { label: "PUT OI / FLOAT", value: `${fmt(p.put_oi_vs_float, 1)}%` },
            ].filter(Boolean);
            return (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, color: C.teal, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 6 }}>
                PLATFORM SIGNALS
                <span style={{ fontSize: 10, color: C.textDim, fontWeight: 400, letterSpacing: 0, marginLeft: 8 }}>
                  {isCandidate ? "nightly pre-score" : "live market intelligence"}
                  {p.scored_at ? ` · ${String(p.scored_at).slice(0, 10)}` : ""}
                  {p._source === "partial" ? " · partial" : ""}
                </span>
              </div>

              {/* ⚠ CONTEXT, NOT AN ERROR. The symbol is simply not in the nightly
                  universe, which is the normal case for anything liquid. Amber and
                  quiet — the dark-pool and options sections below are still real. */}
              {!isCandidate && (
                <div style={{ fontSize: 11, color: C.amber, lineHeight: 1.6, marginBottom: 8 }}>
                  {result.ticker || p.symbol} is not currently a squeeze candidate — showing
                  available market intelligence only.
                </div>
              )}

              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
                {p.total_score != null && (
                  <span style={s.tag(p.total_score >= 80 ? C.red : p.total_score >= 65 ? C.amber : C.textDim)}>
                    COMPOSITE {p.total_score}
                    {p.signal ? ` · ${p.signal}` : ""}
                  </span>
                )}
                {p.si_tier && <span style={s.tag(C.textDim)}>SI TIER: {p.si_tier}</span>}
                {/* ⚠ These three are UNGATED backend-side now — they describe live
                    order flow and populate for any traded symbol, candidate or not. */}
                {p.dark_pool_bias && (
                  <span style={s.tag(p.dark_pool_bias === "BUYER" ? C.green : p.dark_pool_bias === "SELLER" ? C.red : C.textDim)}>
                    DP BIAS: {p.dark_pool_bias}
                  </span>
                )}
                {p.unusual_call_activity === true && <span style={s.tag(C.amber)}>UNUSUAL CALLS</span>}
                {p.early_squeeze === true && <span style={s.tag(C.red)}>⚡ EARLY SQUEEZE — covering may be starting</span>}
              </div>

              {/* ⚠⚠ THREE DISTINCT OPTION STATES. "no listed options" and "the
                  fetch failed" are different facts and must never be conflated —
                  telling a user a symbol has no options when the upstream merely
                  broke is a confident false statement. */}
              {p.no_options ? (
                <div style={{ fontSize: 12, color: C.textDim, textAlign: "center", padding: "10px 0" }}>
                  No listed options for {result.ticker || p.symbol}
                </div>
              ) : p.options_error ? (
                <div style={{ fontSize: 12, color: C.amber, textAlign: "center", padding: "10px 0" }}>
                  Options data temporarily unavailable
                </div>
              ) : tiles.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${tiles.length}, 1fr)`, gap: 8, marginBottom: 8 }}>
                  {tiles.map(({ label, value }) => (
                    <div key={label} style={{ background: "#060a0e", borderRadius: 2, padding: "6px 8px", border: `1px solid ${C.border}` }}>
                      <div style={s.lbl}>{label}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{value}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* ⚠ Stream dark pool — significance-gated institutional blocks only
                  (>=10k shares AND >=$200k), NOT the full-tape REST scan the
                  candidate row's DP line uses. The two can disagree on bias and
                  neither is wrong; they read different slices of the tape.
                  ⚠ A 0/0/0 buyer/seller/midpoint split alongside a real print count
                  is accurate — those counters only exist once a BUYER/SELLER/MIDPOINT
                  print increments them. */}
              {p.dp_net_bias && (
                <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.6 }}>
                  Stream blocks: <span style={{ color: p.dp_net_bias === "BUYER" ? C.green : p.dp_net_bias === "SELLER" ? C.red : C.textDim, fontWeight: 700 }}>{p.dp_net_bias}</span>
                  {p.dp_total_prints != null ? ` · ${p.dp_total_prints} prints` : ""}
                  {p.dp_total_notional != null ? ` · $${fmtK(p.dp_total_notional)}` : ""}
                  {` · ${p.dp_buyer_count ?? 0}B / ${p.dp_seller_count ?? 0}S / ${p.dp_midpoint_count ?? 0}M`}
                </div>
              )}
            </div>
            );
          })()}

          {/* Key metrics grid */}
            {/* ⚠⚠ PLATFORM VALUES ARE A FALLBACK, NEVER AN OVERWRITE, AND THAT IS
                THE WHOLE DESIGN. `result.*` here is the browser-side Yahoo scan;
                `result.platform.*` is the backend's front-expiry chain. Two of the
                Yahoo fields FEED computeScore — otmCallOI drives the OTM-call
                clause and putOI derives putOIvsFloat — so assigning a platform
                value over a populated one would silently move every score. These
                fill EMPTY cells only, computeScore is not re-run, and the score
                is untouched by construction. See the merge warnings above.
                ⚠ The label follows the source: when a cell falls back, it says
                "(FRONT EXP)" because the platform figure is ONE expiry (>= 21 DTE
                via _skew_expiry) while the Yahoo figure is chain-wide. Showing
                both under one label would silently compare different quantities. */}
            {(() => {
              const P = result.platform || {};
              // Yahoo first; platform only when Yahoo produced nothing.
              // ⚠ A Yahoo 0 COUNTS AS NOTHING and falls through, deliberately.
              // These tiles previously rendered `result.putOI ? … : "—"`, so a 0
              // already displayed as "—" — in this payload it means the scrape
              // came back without usable data, not that open interest is genuinely
              // zero. Treating it as present would newly render "0" where the
              // panel has always shown a dash, and would block a real platform
              // figure from filling the cell.
              // ⚠ The platform side uses `!= null`, because there a 0 IS a real
              // reading: no open interest on that particular front expiry.
              // ⚠ Hide-when-null: neither side real → the tile is dropped (no "—").
              // real() also rejects NaN, which `!= null` let through to fmtK → "0".
              const pick = (yahoo, plat) => (real(yahoo) && yahoo !== 0 ? { v: yahoo, p: false }
                : real(plat) ? { v: plat, p: true } : null);
              const oi   = (y, p, yLbl, pLbl) => {
                const r = pick(y, p);
                return r && { label: r.p ? pLbl : yLbl, value: fmtK(r.v) };
              };
              const iv   = (y, p, yLbl, pLbl) => {
                const r = pick(y, p);
                return r && { label: r.p ? pLbl : yLbl, value: `${fmt(r.v * 100, 1)}%` };
              };
              const tiles = [
                real(result.float) && result.float > 0 && { label: "FLOAT", value: fmtK(result.float) },
                // ⚠ float_shares IS EDGAR shares outstanding used as a float proxy
                // — EDGAR exposes no true float. It backfills SHARES OUT only, never
                // FLOAT, so the two tiles cannot silently become the same number.
                // Share counts must be positive on both sides (as in FLOAT (EDGAR)
                // above); open interest and IV below keep a real platform 0.
                oi(result.sharesOut, P.float_shares > 0 ? P.float_shares : null, "SHARES OUT", "SHARES OUT (FILED)"),
                // ⚠ Yahoo only. /api/squeeze/{symbol} returns no avg_vol (only the
                // /candidates rows carry FINRA's), so the old P.avg_vol fallback was
                // dead and is gone.
                real(result.avgVolume) && result.avgVolume > 0 && { label: "AVG VOLUME", value: fmtK(result.avgVolume) },
                oi(result.putOI,     P.total_put_oi, "TOTAL PUT OI",  "PUT OI (FRONT EXP)"),
                oi(result.callOI,    P.total_call_oi,"TOTAL CALL OI", "CALL OI (FRONT EXP)"),
                // ⚠ NOT backfilled from P.otm_call_oi: the platform counts every
                // strike above spot while this means calls >10% OTM, and this value
                // feeds computeScore. Different definitions, one of them scored.
                // A local 0 stays hidden, like every Yahoo 0 above: the scrape
                // coerces missing OI to 0, so it cannot be told from a real zero.
                real(result.otmCallOI) && result.otmCallOI > 0 && { label: "OTM CALL OI", value: fmtK(result.otmCallOI) },
                iv(result.putIVavg,  P.put_iv_avg,  "PUT IV AVG",  "OTM PUT IV (FRONT EXP)"),
                iv(result.callIVavg, P.call_iv_avg, "CALL IV AVG", "ATM CALL IV (FRONT EXP)"),
              ].filter(Boolean);
              if (!tiles.length) return null;
              return (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 8,
                  marginTop: 12,
                  marginBottom: 12,
                }}>
                  {tiles.map(({ label, value }) => (
                    <div key={label} style={{ background: "#060a0e", borderRadius: 2, padding: "6px 8px", border: `1px solid ${C.border}` }}>
                      <div style={s.lbl}>{label}</div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: C.text }}>{value}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

          {/* Top OI strikes */}
          {(result.topPuts.length > 0 || result.topCalls.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              {[
                { data: result.topPuts,  label: "TOP PUT OI STRIKES",  color: C.red   },
                { data: result.topCalls, label: "TOP CALL OI STRIKES", color: C.green },
              ].map(({ data, label, color }) => (
                <div key={label}>
                  <div style={{ fontSize: 11, color, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 5 }}>
                    {label}
                  </div>
                  {data.map((o, i) => (
                    <div key={i} style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "3px 0",
                      borderBottom: `1px solid ${C.border}`,
                      fontSize: 13,
                    }}>
                      <span style={{ color: C.text }}>${o.strike}</span>
                      <span style={{ color: C.textMid }}>{fmtK(o.oi)} OI</span>
                      <span style={{ fontSize: 11, color: C.textDim }}>{o.expiration}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Unusual strikes */}
          {result.unusualStrikes?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.amber, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 5 }}>
                UNUSUAL VOLUME — Vol/OI &gt; 3x
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {["TYPE", "STRIKE", "EXP", "VOL", "OI", "RATIO"].map((h) => (
                      <th key={h} style={{ padding: "2px 5px", textAlign: "right", fontSize: 10, color: C.textDim, fontWeight: 700 }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.unusualStrikes.map((o, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: "4px 5px", textAlign: "right" }}>
                        <span style={s.tag(o.isCall ? C.green : C.red)}>{o.isCall ? "CALL" : "PUT"}</span>
                      </td>
                      <td style={{ padding: "4px 5px", textAlign: "right", fontWeight: 700 }}>${o.strike}</td>
                      <td style={{ padding: "4px 5px", textAlign: "right", color: C.textDim, fontSize: 11 }}>{o.expiration}</td>
                      <td style={{ padding: "4px 5px", textAlign: "right", color: C.amber, fontWeight: 700 }}>{fmtK(o.volume)}</td>
                      <td style={{ padding: "4px 5px", textAlign: "right", color: C.textMid }}>{fmtK(o.oi)}</td>
                      <td style={{ padding: "4px 5px", textAlign: "right", color: C.red, fontWeight: 700 }}>{fmt(o.ratio, 1)}x</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Analyst */}
          {(anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
            <AnalystButton
              anthropicKey={anthropicKey}
              panel="squeeze"
              label="SYNTHESIZE"
              context={analystContext}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── SQUEEZE TAB ──────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
// MARKET-WIDE SQUEEZE SCANNER — candidates + most-shorted basket
// ═══════════════════════════════════════════════════════════════════════════════

const SQ_FILTERS = [
  { label: "ALL", v: 0 }, { label: "WATCH 40+", v: 40 }, { label: "NOTABLE 50+", v: 50 },
  { label: "ELEVATED 65+", v: 65 }, { label: "EXTREME 80+", v: 80 },
];
const sqSigColor = (sc) => (sc >= 80 ? C.red : sc >= 65 ? C.amber : sc >= 50 ? C.text : C.textDim);
const sqBiasArrow = (b) => (b === "BUYER" ? { a: "↑", c: C.green } : b === "SELLER" ? { a: "↓", c: C.red } : { a: "→", c: C.textDim });

// One candidate row — lazily loads its own dark-pool cross-reference and shows it
// compactly, with a deliberate [→ DEEP DIVE] link and no auto-navigation.
// ⚠ onDarkPool is gone from here and from SqueezeCandidates: the → DARK POOL link
// was its only consumer. It is still ACCEPTED by SqueezeTab below, because
// App.jsx:660 passes it and App.jsx is out of scope for this change.
function CandidateRow({ c, onDeepDive, onConvergence }) {
  const [dp, setDp] = useState(undefined); // undefined = loading, null = failed, obj = loaded

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await backendFetch("https://data.alpaca.markets", `/api/squeeze/darkpool/${c.symbol}`);
        const d = await res.json();
        if (alive) setDp(d && d.symbol ? d : null);
      } catch { if (alive) setDp(null); }
    })();
    return () => { alive = false; };
  }, [c.symbol]);

  const convergence = c.total_score >= 65 && dp && dp.net_bias === "BUYER" && dp.cluster_detected;
  useEffect(() => {
    if (convergence && onConvergence) onConvergence(c, dp);
  }, [convergence]); // eslint-disable-line react-hooks/exhaustive-deps

  const sc = sqSigColor(c.total_score);
  const bias = dp ? sqBiasArrow(dp.net_bias) : null;

  return (
    <div onClick={() => onDeepDive(c.symbol)}
      style={{ minHeight: 64, padding: "8px 10px", borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = C.panelAlt)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
      {/* Row 1 — primary metrics */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.amber, minWidth: 46 }}>{c.symbol}</span>
        <span style={{ fontSize: 13, fontWeight: c.total_score >= 65 ? 700 : 400, color: sc }}>{c.total_score}</span>
        <span style={{ fontSize: 10, color: sc }}>{c.signal}</span>
        {/* ⚠ Hide-when-null: si_float is null on DTC-only rows and rendered as a
            fake "SI 0.0%"; a DTC that is not a positive number is not a reading. */}
        {real(c.si_float) && <span style={{ fontSize: 10, color: C.text }}>SI {(c.si_float * 100).toFixed(1)}%</span>}
        {real(c.dtc) && c.dtc > 0 && <span style={{ fontSize: 10, color: C.textDim }}>{c.dtc} days</span>}
        {convergence && (
          <span style={{ fontSize: 8, padding: "2px 6px", border: `1px solid ${C.amber}`, color: C.amber, borderRadius: 2, fontWeight: 700 }}>CONVERGENCE</span>
        )}
      </div>
      {/* Row 2 — dark pool cross-reference */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, fontSize: 9, color: C.textDim, flexWrap: "wrap" }}>
        {dp === undefined ? (
          <span>Loading…</span>
        ) : dp === null ? (
          <span>DP data unavailable</span>
        ) : (
          <span>
            DP: {dp.dark_pct}% · <span style={{ color: bias.c }}>{bias.a}{dp.net_bias}</span>
            {" · "}{dp.cluster_detected ? <span style={{ color: C.amber }}>CLUSTER ●</span> : <span style={{ color: C.textDim }}>no cluster</span>}
          </span>
        )}
        {/* ⚠ Rendered OUTSIDE the dp ternary, so it shows on every candidate. The
            old → DARK POOL link lived inside the dp-present branch, which meant the
            one shortcut a row offered vanished exactly when its data was thin.
            ⚠ This calls the SAME handler as the row itself (:807), so it is a
            visible affordance for what the whole row already does, not new
            behaviour — the rows carried no cue that they were clickable.
            ⚠ stopPropagation is retained deliberately though it is now inert: both
            handlers are identical, so it only suppresses a duplicate call. */}
        <span onClick={(e) => { e.stopPropagation(); onDeepDive(c.symbol); }}
          style={{ color: C.amber, cursor: "pointer", fontWeight: 700 }}>→ DEEP DIVE</span>
      </div>
    </div>
  );
}

function SqueezeCandidates({ onData, onDeepDive }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter]   = useState(0);
  const pushed = useRef(new Set());

  const fetchCandidates = useCallback(async (enrich) => {
    setLoading(true);
    try {
      const res = await backendFetch("https://data.alpaca.markets",
        `/api/squeeze/candidates?limit=25&min_score=40${enrich ? "&enrich=true" : ""}`);
      setData(await res.json());
    } catch { setData({ candidates: [] }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCandidates(false); }, [fetchCandidates]);

  const onConvergence = useCallback((c, dp) => {
    if (c.total_score >= 80 && dp?.net_bias === "BUYER" && dp?.cluster_detected && !pushed.current.has(c.symbol)) {
      pushed.current.add(c.symbol);
      if (onData) onData("squeezeConvergence", {
        symbol: c.symbol, score: c.total_score, si_float: c.si_float,
        dp_bias: dp.net_bias, cluster_notional: dp.cluster_notional,
      });
    }
  }, [onData]);

  const cands = (data?.candidates || []).filter((c) => c.total_score >= filter);
  const updated = data?.scored_at ? new Date(data.scored_at).toLocaleString() : "—";

  return (
    <div>
      <div style={{ fontSize: 11, color: C.amber, fontWeight: 700, letterSpacing: "0.15em" }}>SQUEEZE CANDIDATES</div>
      <div style={{ fontSize: 9, color: C.textDim, marginBottom: 8 }}>
        Pre-scored from short interest · Top {cands.length} candidates · Updated {updated}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        <button style={{ ...s.btn("amber"), padding: "4px 12px" }} onClick={() => fetchCandidates(true)} disabled={loading}>
          {loading ? "SCORING…" : "REFRESH"}
        </button>
        {SQ_FILTERS.map((f) => (
          <button key={f.label} onClick={() => setFilter(f.v)} style={{ ...s.btn(filter === f.v ? "amber" : "default"), padding: "3px 8px", fontSize: 10 }}>{f.label}</button>
        ))}
      </div>
      <div style={s.panel}>
        <div style={s.panelB}>
          {data?.computing ? (
            <div style={{ fontSize: 10, color: C.textDim, textAlign: "center", padding: 16 }}>
              Pre-scoring the squeeze universe from short interest — check back shortly.
            </div>
          ) : !cands.length ? (
            <div style={{ fontSize: 10, color: C.textDim, textAlign: "center", padding: 16 }}>No squeeze candidates at this filter.</div>
          ) : cands.map((c) => (
            <CandidateRow key={c.symbol} c={c} onDeepDive={onDeepDive} onConvergence={onConvergence} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SqueezeBasket() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await backendFetch("https://data.alpaca.markets", "/api/squeeze/basket");
        setData(await res.json());
      } catch { setData({ components: [] }); }
    })();
  }, []);

  if (data?.computing) return <div style={{ fontSize: 10, color: C.textDim, padding: 16, textAlign: "center" }}>Building the most-shorted basket — check back shortly.</div>;
  if (!data) return <div style={{ fontSize: 10, color: C.textDim, padding: 16, textAlign: "center" }}>Loading basket…</div>;

  // ⚠ Hide-when-null: a missing return is not "0.00%" and is never coloured; the
  // bar, spread and its legend render only with a real spread.
  const hasBasket = real(data.basket_return_pct);
  const hasSpy = real(data.spy_return_pct);
  const spread = real(data.spread_pct) ? data.spread_pct : null;
  const dir = data.spread_direction;
  const pressColor = dir === "BASKET_LEADING" ? C.red : dir === "SPY_LEADING" ? C.green : C.textDim;
  const pressMsg = dir === "BASKET_LEADING" ? "Shorts covering — squeeze pressure building"
    : dir === "SPY_LEADING" ? "Short thesis working — shorts winning"
    : "No clear directional signal";
  const clamp = Math.max(-3, Math.min(3, spread ?? 0));
  const fillPct = ((clamp + 3) / 6) * 100;
  const comps = [...(data.components || [])].sort((a, b) => (b.si_float || 0) - (a.si_float || 0));

  return (
    <div>
      <div style={{ fontSize: 11, color: C.amber, fontWeight: 700, letterSpacing: "0.15em" }}>MOST SHORTED BASKET</div>
      <div style={{ fontSize: 9, color: C.textDim, marginBottom: 10 }}>Top 50 by SI/Float · Equal weighted</div>

      <div style={{ ...s.panel, marginBottom: 10 }}>
        <div style={{ ...s.panelB, textAlign: "center" }}>
          <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.12em", marginBottom: 8 }}>BASKET vs SPY TODAY</div>
          {(hasBasket || hasSpy || spread != null) && (
          <div style={{ display: "flex", justifyContent: "center", gap: 24, marginBottom: 10, flexWrap: "wrap" }}>
            {hasBasket && <div><div style={{ fontSize: 9, color: C.textDim }}>BASKET</div><div style={{ fontSize: 20, fontWeight: 700, color: data.basket_return_pct >= 0 ? C.green : C.red }}>{data.basket_return_pct.toFixed(2)}%</div></div>}
            {hasSpy && <div><div style={{ fontSize: 9, color: C.textDim }}>SPY</div><div style={{ fontSize: 20, fontWeight: 700, color: data.spy_return_pct >= 0 ? C.green : C.red }}>{data.spy_return_pct.toFixed(2)}%</div></div>}
            {spread != null && <div><div style={{ fontSize: 9, color: C.textDim }}>SPREAD</div><div style={{ fontSize: 20, fontWeight: 700, color: pressColor }}>{spread >= 0 ? "+" : ""}{spread.toFixed(2)}%</div></div>}
          </div>
          )}
          {spread != null && (
          <>
          <div style={{ position: "relative", height: 8, background: C.border, borderRadius: 4, marginBottom: 4 }}>
            <div style={{ position: "absolute", left: "50%", top: -2, bottom: -2, width: 1, background: C.textDim }} />
            <div style={{ position: "absolute", left: `${Math.min(50, fillPct)}%`, width: `${Math.abs(fillPct - 50)}%`, top: 0, bottom: 0, background: pressColor, borderRadius: 4 }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 8, color: C.textDim, marginBottom: 8 }}><span>◀ SPY leading</span><span>basket leading ▶</span></div>
          </>
          )}
          <div style={{ fontSize: 13, fontWeight: 700, color: pressColor }}>SQUEEZE PRESSURE: {data.squeeze_pressure || "LOW"}</div>
          <div style={{ fontSize: 9, color: C.textDim }}>{pressMsg}</div>
        </div>
      </div>

      <div style={s.panel}>
        <div onClick={() => setOpen((o) => !o)} style={{ ...s.panelB, cursor: "pointer", fontSize: 10, color: C.textDim }}>
          BASKET COMPONENTS ({comps.length} names) {open ? "▾" : "▸"}
        </div>
        {open && (
          <div style={{ ...s.panelB, paddingTop: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
              <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["TICKER", "SI/FLOAT", "TODAY %"].map((h, i) => <th key={h} style={{ textAlign: i === 0 ? "left" : "right", padding: "3px 6px", fontSize: 9, color: C.textDim, fontWeight: 700 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {comps.map((cp) => (
                  <tr key={cp.symbol} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "3px 6px", color: C.amber, fontWeight: 700 }}>{cp.symbol}</td>
                    {/* Missing → empty cell (no placeholder, no colour); the row stays. */}
                    <td style={{ padding: "3px 6px", textAlign: "right", color: C.text }}>{real(cp.si_float) ? `${(cp.si_float * 100).toFixed(1)}%` : ""}</td>
                    <td style={{ padding: "3px 6px", textAlign: "right", color: !real(cp.return_pct) ? C.textDim : cp.return_pct >= 0 ? C.green : C.red }}>
                      {real(cp.return_pct) ? `${cp.return_pct >= 0 ? "+" : ""}${cp.return_pct.toFixed(2)}%` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// The user's custom Squeeze scan list — persisted, never seeded with defaults.
const SCAN_LIST_KEY = "qs_squeeze_scan_list";
function loadScanList() {
  try {
    const raw = JSON.parse(localStorage.getItem(SCAN_LIST_KEY));
    return Array.isArray(raw) ? raw.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

// ⚠ onDarkPool is accepted but NO LONGER CONSUMED anywhere in this file — the
// → DARK POOL link that used it is now → DEEP DIVE. Retained in the signature
// because App.jsx:660 still passes it and App.jsx is out of scope here; dropping
// it would leave a live call site handing a prop to a component that ignores it.
// ⚠ Squeeze no longer routes to the Dark Pool tab at all. dpFocus is NOT orphaned
// — the Convergence Board still sets it — but this was the other entry point.
export function SqueezeTab({ keys, onData, onDarkPool }) {
  const [scanInput,   setScanInput]   = useState("");
  const [scanList,    setScanList]    = useState(loadScanList);
  const [results,     setResults]     = useState({});
  const [scanning,    setScanning]    = useState(false);
  const [expanded,    setExpanded]    = useState({});
  const [finraRaw,    setFinraRaw]    = useState("");
  const [finraData,   setFinraData]   = useState(null);
  const [finraParsed, setFinraParsed] = useState(false);
  const [showFinra,   setShowFinra]   = useState(false);
  const [sortBy,      setSortBy]      = useState("score");
  const [view,           setView]           = useState("candidates");  // candidates | basket | deepdive
  const [deepDiveTicker, setDeepDiveTicker] = useState(null);

  // Deep-dive a single ticker (from a tapped candidate) — preserves the existing
  // scan-list workflow; just adds this symbol and scores it.
  const deepDive = useCallback(async (sym) => {
    setView("deepdive");
    setDeepDiveTicker(sym);
    setScanList((prev) => (prev.includes(sym) ? prev : [...prev, sym]));
    setResults((prev) => ({ ...prev, [sym]: { ...emptyResult(sym), status: "scanning" } }));
    const result = await scanWithPlatform(sym, keys, finraData);
    setResults((prev) => ({ ...prev, [sym]: result }));
  }, [keys, finraData]);

  // Persist the user's scan list on every change (add / remove / deep-dive).
  // No defaults are seeded — an empty list is valid and the user controls it.
  useEffect(() => {
    try { localStorage.setItem(SCAN_LIST_KEY, JSON.stringify(scanList)); } catch { /* quota */ }
  }, [scanList]);

  // ── Parse FINRA paste ──
  const parseFinra = () => {
    if (!finraRaw.trim()) return;
    const parsed = parseFinraData(finraRaw);
    setFinraData(parsed);
    setFinraParsed(true);
  };

  // ── Add tickers to scan list ──
  const addToList = () => {
    const syms = scanInput.toUpperCase().split(/[\s,]+/).filter(Boolean);
    setScanList((prev) => [...new Set([...prev, ...syms])]);
    setScanInput("");
  };

  const removeFromList = (sym) => {
    setScanList((prev) => prev.filter((s) => s !== sym));
    setResults((prev) => { const n = { ...prev }; delete n[sym]; return n; });
  };

  // ── Run full scan ──
  const runScan = async () => {
    if (!scanList.length) return;
    setScanning(true);

    // Mark all as scanning
    const initial = {};
    scanList.forEach((sym) => { initial[sym] = emptyResult(sym); initial[sym].status = "scanning"; });
    setResults(initial);
    setExpanded({});

    // Scan sequentially to avoid rate limiting
    const allResults = {};
    for (const sym of scanList) {
      const result = await scanWithPlatform(sym, keys, finraData);
      allResults[sym] = result;
      setResults((prev) => ({ ...prev, [sym]: result }));
      // Small delay between tickers
      await new Promise((r) => setTimeout(r, 800));
    }

    // Push to signal board
    if (onData) {
      onData("squeezeScans", Object.values(allResults).filter((r) => r.score >= 40));
    }

    setScanning(false);
  };

  // ── Sorted results ──
  // ⚠ sortBy/setSortBy are retained — "score" still uses them, and the ticker
  // fallback below is what an unrecognised value (e.g. a stale persisted one)
  // resolves to. Only the si / dtc / synthetic branches were removed.
  const sortedResults = Object.values(results).sort((a, b) => {
    if (sortBy === "score")  return b.score - a.score;
    return a.ticker.localeCompare(b.ticker);
  });

  const scannedCount  = Object.values(results).filter((r) => r.status === "done").length;
  const flaggedCount  = Object.values(results).filter((r) => r.score >= 60).length;
  const extremeCount  = Object.values(results).filter((r) => r.score >= 80).length;
  const dpDeepSymbol  = deepDiveTicker || sortedResults[0]?.ticker || null;

  return (
    <div>
      {/* View tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 10, borderBottom: `1px solid ${C.border}` }}>
        {[["candidates", "CANDIDATES"], ["basket", "BASKET"], ["deepdive", "DEEP DIVE"]].map(([v, l]) => (
          <div key={v} onClick={() => setView(v)}
            style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", padding: "6px 12px", cursor: "pointer",
              color: view === v ? C.amber : C.textMid, borderBottom: `2px solid ${view === v ? C.amber : "transparent"}`, marginBottom: -1 }}>
            {l}
          </div>
        ))}
      </div>

      {view === "candidates" && (
        <SqueezeCandidates onData={onData} onDeepDive={deepDive} />
      )}
      {view === "basket" && <SqueezeBasket />}

      {view === "deepdive" && (
        <>
      {/* Header */}
      <div style={{ ...s.panel, padding: "10px 12px", marginBottom: 10 }}>
        <div style={{ fontSize: 14, color: C.red, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 4 }}>
          ◈ SYNTHETIC SHORT & SQUEEZE SCANNER
        </div>
        <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.7, marginBottom: 6 }}>
          Detects synthetic short setups — high short interest, put OI exceeding float, IV skew indicating borrow stress, and OTM call accumulation.
          The GME squeeze had short interest at ~140% of float with put OI exceeding the float — both signals this scanner detects.
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 11, color: C.textDim, padding: "3px 8px", background: C.panelAlt, borderRadius: 2, border: `1px solid ${C.border}` }}>
            Short Interest: <span style={{ color: C.amber }}>Live + pasted file (2-week lag)</span>
          </div>
          <div style={{ fontSize: 11, color: C.textDim, padding: "3px 8px", background: C.panelAlt, borderRadius: 2, border: `1px solid ${C.border}` }}>
            Options OI/IV: <span style={{ color: C.green }}>Live</span>
          </div>
          <div style={{ fontSize: 11, color: C.textDim, padding: "3px 8px", background: C.panelAlt, borderRadius: 2, border: `1px solid ${C.border}` }}>
            Synthetic detection: <span style={{ color: C.teal }}>Put OI × 100 vs Float</span>
          </div>
        </div>
      </div>

      {/* FINRA data paste */}
      <div style={s.panel}>
        <div
          style={{ ...s.panelH, cursor: "pointer" }}
          onClick={() => setShowFinra((p) => !p)}
        >
          <span style={s.panelT}>
            SHORT INTEREST DATA
            {finraParsed && (
              <span style={{ ...s.tag(C.green), marginLeft: 8, fontSize: 11 }}>
                ✓ {Object.keys(finraData || {}).length} tickers loaded
              </span>
            )}
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ ...s.tag(C.amber), fontSize: 11 }}>~2 WEEK LAG · STRUCTURAL CONFIRMATION</span>
            <span style={{ fontSize: 13, color: C.textDim }}>{showFinra ? "▲" : "▼"}</span>
          </div>
        </div>

        {showFinra && (
          <div style={s.panelB}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 8, lineHeight: 1.7 }}>
              Paste the raw short interest file below — pipe-delimited or CSV accepted.
              Format: Symbol | Company | Date | ShortInterest | AvgDailyVol | DaysTocover
            </div>
            <div style={{ fontSize: 11, color: C.textDim, marginBottom: 4 }}>
              {finraRaw.length > 0 ? `${finraRaw.split("\n").length} lines pasted` : "No data pasted"}
            </div>
            <textarea
              style={{ ...s.inp, height: 100, resize: "vertical", marginBottom: 8, fontSize: 13 }}
              placeholder="Paste short interest data here..."
              value={finraRaw}
              onChange={(e) => { setFinraRaw(e.target.value); setFinraParsed(false); }}
            />
            <div style={{ display: "flex", gap: 6 }}>
              <button style={s.btn("amber")} onClick={parseFinra} disabled={!finraRaw.trim()}>
                PARSE DATA
              </button>
              {finraParsed && (
                <button style={s.btn()} onClick={() => { setFinraRaw(""); setFinraData(null); setFinraParsed(false); }}>
                  CLEAR
                </button>
              )}
            </div>
            {finraParsed && (
              <div style={{ fontSize: 11, color: C.green, marginTop: 6 }}>
                ✓ Parsed {Object.keys(finraData || {}).length} tickers from the pasted file.
                Short interest will override estimates where pasted data exists.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scan controls */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={s.panelT}>SCAN LIST</span>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {scannedCount > 0 && (
              <span style={{ fontSize: 11, color: C.textDim }}>
                {scannedCount}/{scanList.length} scanned ·{" "}
                <span style={{ color: flaggedCount > 0 ? C.orange : C.textDim }}>{flaggedCount} flagged</span>
                {extremeCount > 0 && <span style={{ color: C.red }}> · {extremeCount} extreme</span>}
              </span>
            )}
            <button
              style={{ ...s.btn("amber"), padding: "4px 14px" }}
              onClick={runScan}
              disabled={scanning || !scanList.length}
            >
              {scanning ? `SCANNING ${scannedCount}/${scanList.length}...` : "RUN SCAN"}
            </button>
          </div>
        </div>
        <div style={s.panelB}>
          {/* Add tickers */}
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <input
              style={{ ...s.inp, flex: 1 }}
              placeholder="Add tickers to scan..."
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && addToList()}
            />
            <button style={s.btn("amber")} onClick={addToList}>ADD</button>
          </div>

          {/* Ticker chips (or empty-state prompt when no tickers set) */}
          {scanList.length === 0 ? (
            <div style={{ fontSize: 10, color: C.textDim, marginBottom: 8, lineHeight: 1.6 }}>
              Add tickers to your scan list or use the pre-scored universe from the CANDIDATES view.
            </div>
          ) : (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
            {scanList.map((sym) => {
              const r     = results[sym];
              const tier  = r ? getTier(r.score) : null;
              return (
                <div key={sym} style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "3px 8px",
                  background: tier ? tier.color + "15" : C.panelAlt,
                  border: `1px solid ${tier ? tier.color + "44" : C.border}`,
                  borderRadius: 2,
                  fontSize: 13,
                }}>
                  <span style={{ fontWeight: 700, color: tier ? tier.color : C.text }}>{sym}</span>
                  {r?.status === "scanning" && <span style={{ fontSize: 10, color: C.textDim }}>...</span>}
                  {r?.status === "done" && <span style={{ fontSize: 11, color: tier?.color }}>{r.score}</span>}
                  <button
                    style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", padding: 0, fontSize: 13, fontFamily: font }}
                    onClick={() => removeFromList(sym)}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
          )}

          <div style={{ fontSize: 11, color: C.textDim }}>
            Scans {scanList.length} ticker{scanList.length !== 1 ? "s" : ""} ·
            Pulls summary + options chain (3 expirations) per ticker ·
            ~{Math.ceil(scanList.length * 1.5)} seconds estimated
          </div>
        </div>
      </div>

      {/* Results */}
      {Object.keys(results).length > 0 && (
        <div>
          {/* Sort controls */}
          <div style={{ display: "flex", gap: 5, marginBottom: 8, alignItems: "center" }}>
            <span style={{ fontSize: 11, color: C.textDim }}>SORT BY:</span>
            {[
              ["score",     "SQUEEZE SCORE"],
            ].map(([key, label]) => (
              <button
                key={key}
                style={{ ...s.btn(sortBy === key ? "amber" : "default"), padding: "3px 8px", fontSize: 11 }}
                onClick={() => setSortBy(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {sortedResults.map((result) => (
            <ResultCard
              key={result.ticker}
              result={result}
              anthropicKey={keys.anthropicKey}
              expanded={!!expanded[result.ticker]}
              onToggle={() => setExpanded((p) => ({ ...p, [result.ticker]: !p[result.ticker] }))}
            />
          ))}
        </div>
      )}

      {/* Methodology */}
      <div style={s.panel}>
        <div style={s.panelH}><span style={s.panelT}>SIGNAL METHODOLOGY</span></div>
        <div style={{
          ...s.panelB,
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 10,
        }}>
          {[
            {
              title: "SHORT INTEREST > FLOAT",
              color: C.red,
              desc: "If short interest exceeds 100% of float, synthetic shares must exist — mathematically impossible otherwise. Brokers create synthetic longs to facilitate excess shorts. GME hit ~140%. This is the primary structural signal.",
            },
            {
              title: "PUT OI × 100 > FLOAT",
              color: C.red,
              desc: "Each put contract represents 100 shares. If total put OI × 100 exceeds the float, the puts cannot all be covered by real shares. Confirms synthetic put creation — dealers are short more than exists. The second confirmation of the GME setup.",
            },
            {
              title: "PUT/CALL IV SKEW",
              color: C.amber,
              desc: "When short interest is extreme, borrow costs spike. Elevated borrow cost flows directly into put premiums — puts become more expensive relative to calls. Put IV / Call IV ratio above 1.5x is a real-time proxy for borrow stress, updating continuously unlike short interest reports.",
            },
            {
              title: "DAYS TO COVER",
              color: C.amber,
              desc: "Short interest divided by average daily volume. Tells you how many days it would take all shorts to exit at normal volume. Above 10 days = significant exit pain. Above 20 = squeeze fuel. Shorts cannot unwind without moving price against themselves.",
            },
            {
              title: "OTM CALL BUILDUP",
              color: C.teal,
              desc: "Accumulation of open interest on cheap out-of-the-money calls is a positioning signal — someone is buying upside exposure. In GME, OTM call OI built steadily before the squeeze. Combined with high short interest it creates a Gamma squeeze mechanic: dealer call hedging buying pushes price into more call hedging.",
            },
            {
              title: "SHORT INTEREST DATA LAG",
              color: C.textDim,
              desc: "Short interest reports are published twice monthly with ~2 week lag. This is the structural confirmation layer — it tells you the position exists. The real-time layer is the options chain: IV skew, put OI, and unusual volume update continuously and act as the timing signal. Together they are more powerful than either alone.",
            },
          ].map((m) => (
            <div key={m.title} style={{ background: "#040608", border: `1px solid ${C.border}`, borderRadius: 3, padding: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: m.color, marginBottom: 5, letterSpacing: "0.08em" }}>
                {m.title}
              </div>
              <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.7 }}>
                {m.desc}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 11, color: C.textDim, marginTop: 6, lineHeight: 1.6 }}>
        <span style={{ color: "#FFD700", fontWeight: 700 }}>Squeeze Pressure Score is a composite signal metric, not a prediction or recommendation.</span>{" "}
        High scores indicate structural conditions historically associated with squeeze setups — they do not predict timing, magnitude, or whether a squeeze will occur.
        Short interest data is updated periodically and may lag.
        Official short interest reports are twice-monthly. Options data is live or 15-min delayed.{" "}
        <span style={{ color: "#FFD700", fontWeight: 700 }}>Not a recommendation — consult a licensed financial advisor for decisions.</span>
      </div>

      {/* Dark pool cross-reference — full convergence display (Option A). The
          circuit breaker was navigating here; no extra tap needed at this point. */}
      {dpDeepSymbol && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: C.amber, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 6 }}>
            DARK POOL CROSS-REFERENCE
          </div>
          <DarkPoolPanel key={dpDeepSymbol} keys={keys} onData={onData} initialSymbol={dpDeepSymbol} />
        </div>
      )}
        </>
      )}
    </div>
  );
}

// ⚠ TEST-ONLY named export, read by harness/squeeze_render.mjs (hide-when-null render
// and score checks). The app imports only SqueezeTab.
export const __squeezeTest = { ResultCard, CandidateRow, SqueezeBasket, computeScore, fetchOptionsMetrics, emptyResult };
